import { useCallback } from "react";
import type { MouseEvent } from "react";
import { useHistory } from "react-router-dom";
import type { History } from "history";

// `button` is optional so keyboard-triggered events (antd menu items) count as
// a plain activation unless a modifier is held.
type ClickLike = Pick<MouseEvent, "ctrlKey" | "metaKey" | "shiftKey" | "altKey"> & {
  button?: number;
};

/**
 * True when the browser should open the target itself (new tab / window)
 * instead of the SPA navigating in place: Ctrl+Click, Cmd+Click on macOS,
 * Shift/Alt+Click, or any non-primary button (middle-click). Mirrors the check
 * react-router's <Link> uses, so links and imperative handlers behave alike.
 */
export const isNewTabClick = (event?: ClickLike | null): boolean =>
  !!event &&
  ((event.button ?? 0) !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey);

const routeHref = (history: History, to: string) => {
  const { pathname, search, hash } = new URL(to, window.location.origin);
  return history.createHref({ pathname, search, hash });
};

/**
 * Props that turn an element into a real link to an in-app route: a proper
 * `href` (so Ctrl/Cmd/middle-click and the right-click "Open in new tab" menu
 * work natively) plus an onClick that keeps plain clicks as SPA navigation.
 * Spread onto antd <Button> (it renders an <a> when given `href`) or an <a>.
 *
 * `state` only reaches plain-click navigations — a new tab starts with empty
 * router state, so target pages must be able to load from the URL alone.
 */
export const useRouteLink = () => {
  const history = useHistory();

  return useCallback(
    (to: string, state?: unknown) => ({
      href: routeHref(history, to),
      onClick: (event: MouseEvent<HTMLElement>) => {
        if (isNewTabClick(event)) return;
        event.preventDefault();
        history.push(to, state);
      },
    }),
    [history]
  );
};

/**
 * Imperative variant for click targets that cannot be links (list rows,
 * dropdown options): opens `to` in a new tab on a new-tab click, otherwise
 * pushes it onto the SPA history.
 */
export const useOpenRoute = () => {
  const history = useHistory();

  return useCallback(
    (to: string, event?: ClickLike | null, state?: unknown) => {
      if (isNewTabClick(event)) {
        window.open(routeHref(history, to), "_blank", "noopener,noreferrer");
        return;
      }
      history.push(to, state);
    },
    [history]
  );
};
